/**
 * useWebSearchStore — per-conversation web-search toggle state.
 *
 * The "网页检索" toggle is session-scoped: once enabled for a conversation,
 * every subsequent send in that conversation forwards
 * `enabled_tools: ["web-search"]` to the engine, until the user toggles it off.
 * State is keyed by conversation id so switching conversations does not leak
 * the toggle; a brand-new conversation starts with the toggle OFF.
 *
 * Persisted to localStorage so the toggle survives tab close / page reload —
 * a user who enabled web search for a conversation expects it to still be on
 * when they reopen that conversation. State is a single record keyed by
 * conversation id; storage size is bounded by the number of conversations a
 * user has toggled (not the total conversation count).
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** The luckyclaw skill id forwarded as an enabled tool when the toggle is on. */
export const WEB_SEARCH_TOOL_ID = "web-search";

interface WebSearchState {
  /** conversationId → active flag. Absent key ⇒ default OFF. */
  activeByConversation: Record<string, boolean>;
  /** Read the toggle for a conversation (defaults to false). */
  isActive: (conversationId: string) => boolean;
  /** Flip the toggle for a conversation. */
  toggle: (conversationId: string) => void;
  /** Explicitly set the toggle (used by tests / programmatic flows). */
  setActive: (conversationId: string, active: boolean) => void;
}

export const useWebSearchStore = create<WebSearchState>()(
  persist(
    (set, get) => ({
      activeByConversation: {},
      isActive: (conversationId) => get().activeByConversation[conversationId] === true,
      toggle: (conversationId) =>
        set((s) => ({
          activeByConversation: {
            ...s.activeByConversation,
            [conversationId]: !s.activeByConversation[conversationId],
          },
        })),
      setActive: (conversationId, active) =>
        set((s) => ({
          activeByConversation: {
            ...s.activeByConversation,
            [conversationId]: active,
          },
        })),
    }),
    {
      name: "jerry-web-search-toggle",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
